import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleAuthProvider, signInWithPopup, signOut, signInWithCustomToken } from 'firebase/auth';
import { auth } from '@clareefai/api/firebase';
import { CountrySelect } from '@clareefai/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useTrialSignupMutation } from '../../store/trialApi';
import PageTitle from '../../components/shared/PageTitle';

const CONSUMER_DOMAINS = new Set([
  'gmail.com','googlemail.com','yahoo.com','yahoo.fr','yahoo.co.uk','yahoo.es','yahoo.de',
  'hotmail.com','hotmail.fr','hotmail.co.uk','outlook.com','outlook.fr','live.com','msn.com',
  'icloud.com','me.com','mac.com','aol.com','protonmail.com','proton.me',
  'gmx.com','gmx.de','gmx.net','mail.com','yandex.com','yandex.ru',
  'qq.com','163.com','126.com','sina.com','foxmail.com',
  'tutanota.com','tutanota.de','tutamail.com',
  'guerrillamail.com','mailinator.com','throwaway.email','temp-mail.org','fakeinbox.com','sharklasers.com',
]);

function isConsumerEmail(email: string) {
  if (import.meta.env.VITE_SKIP_DOMAIN_VALIDATION === 'true') return false;
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  return CONSUMER_DOMAINS.has(domain);
}

const inputCls =
  'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition';

export default function TrialSignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuthLoading, authError, exchangeGoogleIdToken } = useAuth();
  const [trialSignup, { isLoading }] = useTrialSignupMutation();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') ?? '';

  const [step, setStep] = useState<1 | 2>(1);
  const [error, setError] = useState('');
  // Set when the backend (or Firebase itself, for Google) reports that an account
  // already exists for this email — renders a specific, actionable message with a
  // link to /login instead of the generic error box.
  const [emailExistsError, setEmailExistsError] = useState(false);
  const [googleIdToken, setGoogleIdToken] = useState<string | null>(null);
  const [googleEmail, setGoogleEmail] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  // Set once the custom token sign-in resolves; we then wait for AuthContext's
  // onAuthStateChanged listener to pick up the new session before navigating,
  // otherwise PrivateRoute reads the stale "signed out" state and bounces to /login.
  const [awaitingSession, setAwaitingSession] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    job_title: '',
    country: '',
    referral_source: '',
    email: emailFromQuery,
    password: '',
  });

  const set =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setError('');
      setEmailExistsError(false);
    };

  const handleGoogleClick = async () => {
    setError('');
    setEmailExistsError(false);
    setGoogleLoading(true);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const gEmail = result.user.email ?? '';
      if (isConsumerEmail(gEmail)) {
        await signOut(auth);
        setError(t('signup.businessEmailRequired'));
        return;
      }
      const token = await result.user.getIdToken();

      // An existing vendor account for this email means this Google click is a
      // login, not a signup — exchange for that account's own custom token and
      // go straight to the dashboard instead of showing the signup form again.
      const exchange = await exchangeGoogleIdToken(token);
      if (exchange.ok && exchange.customToken) {
        await signInWithCustomToken(auth, exchange.customToken);
        setAwaitingSession(true);
        return;
      }

      // No existing account (or exchange failed) — proceed as a new signup.
      setGoogleIdToken(token);
      setGoogleEmail(gEmail);
      setStep(2);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return;
      if (code === 'auth/account-exists-with-different-credential') {
        await signOut(auth).catch(() => {});
        setEmailExistsError(true);
        return;
      }
      setError(t('signup.errorGeneric'));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleStep1Next = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailExistsError(false);
    if (isConsumerEmail(form.email)) {
      setError(t('signup.businessEmailRequired'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('signup.passwordMin'));
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    setError('');
    setEmailExistsError(false);
    if (googleIdToken) {
      signOut(auth).catch(() => {});
      setGoogleIdToken(null);
      setGoogleEmail('');
    }
    setStep(1);
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailExistsError(false);
    const language = navigator.language.startsWith('fr') ? 'fr' : 'en';

    try {
      if (googleIdToken) {
        const res = await trialSignup({
          first_name: form.first_name,
          last_name: form.last_name,
          email: googleEmail,
          company_name: form.company_name,
          job_title: form.job_title,
          country: form.country,
          referral_source: form.referral_source || undefined,
          google_id_token: googleIdToken,
          language,
        }).unwrap();
        if (res.customToken) {
          await signInWithCustomToken(auth, res.customToken);
          setAwaitingSession(true);
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        await trialSignup({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          company_name: form.company_name,
          job_title: form.job_title,
          country: form.country,
          referral_source: form.referral_source || undefined,
          password: form.password,
          language,
        }).unwrap();
        navigate('/trial/verify-email', { state: { email: form.email } });
      }
    } catch (err: unknown) {
      const data = (err as { data?: { message?: string; code?: string } })?.data;
      if (data?.code === 'EMAIL_EXISTS') {
        setEmailExistsError(true);
        return;
      }
      const msg = data?.message;
      if (msg?.toLowerCase().includes('domain') || msg?.toLowerCase().includes('email')) {
        setError(t('signup.domainNotAllowed'));
      } else {
        setError(msg || t('signup.errorGeneric'));
      }
    }
  };

  if (awaitingSession) {
    if (isAuthenticated && !isAuthLoading) {
      return <Navigate to="/dashboard" replace />;
    }
    if (authError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center">
            <ErrorBox message={authError} />
            <button
              type="button"
              onClick={() => setAwaitingSession(false)}
              className="mt-4 w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition-colors"
            >
              {t('signup.back')}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-violet-500 border-t-transparent" />
      </div>
    );
  }

  const progress = (step / 2) * 100;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PageTitle title={t('signup.pageTitle')} />

      {/* Nav */}
      <header className="flex items-center justify-between px-6 py-5 max-w-6xl mx-auto w-full">
        <a href="https://clareefai.com">
          <img src="/logo-color.png" alt="Clareefai" className="h-8 w-auto" />
        </a>
        <p className="text-sm text-slate-500">
          {t('signup.alreadyHaveAccount')}{' '}
          <Link
            to="/login"
            className="text-violet-600 hover:text-violet-700 font-medium transition-colors"
          >
            {t('signup.signInLink')}
          </Link>
        </p>
      </header>

      <div className="flex-1 flex items-start justify-center px-4 pt-6 pb-16">
        <div className="w-full max-w-lg">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
              <span>{step === 1 ? t('signup.step1of2') : t('signup.step2of2')}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-violet-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{t('signup.title')}</h1>
            <p className="text-sm text-slate-500 mb-8">{t('signup.subtitle')}</p>

            {/* Step 1 — Auth */}
            {step === 1 && (
              <div className="space-y-5">
                {/* Google — primary CTA */}
                <button
                  type="button"
                  onClick={handleGoogleClick}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-4 rounded-xl bg-white border-2 border-violet-200 hover:border-violet-400 hover:bg-violet-50 text-slate-800 font-semibold text-sm transition-all disabled:opacity-50 shadow-sm"
                >
                  <GoogleIcon />
                  {googleLoading ? t('signup.submitting') : t('signup.googleCta')}
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <div className="flex-1 h-px bg-gray-200" />
                  {t('signup.orSignUpWithEmail')}
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Email / password */}
                <form onSubmit={handleStep1Next} className="space-y-4">
                  <Field label={t('signup.email')} required>
                    <input
                      type="email"
                      value={form.email}
                      onChange={set('email')}
                      placeholder={t('signup.emailPlaceholder')}
                      required
                      className={inputCls}
                      autoComplete="email"
                    />
                  </Field>
                  <Field label={t('signup.password')} required>
                    <input
                      type="password"
                      value={form.password}
                      onChange={set('password')}
                      placeholder={t('signup.passwordPlaceholder')}
                      required
                      minLength={8}
                      className={inputCls}
                      autoComplete="new-password"
                    />
                  </Field>

                  {emailExistsError ? <EmailExistsErrorBox t={t} /> : error && <ErrorBox message={error} />}

                  <button
                    type="submit"
                    className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition-colors"
                  >
                    {t('signup.next')}
                  </button>
                </form>
              </div>
            )}

            {/* Step 2 — Profile */}
            {step === 2 && (
              <form onSubmit={handleStep2Submit} className="space-y-5">
                {googleIdToken && (
                  <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {t('signup.googleConnected')} {googleEmail}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('signup.firstName')} required>
                    <input
                      type="text"
                      value={form.first_name}
                      onChange={set('first_name')}
                      placeholder={t('signup.firstNamePlaceholder')}
                      required
                      className={inputCls}
                    />
                  </Field>
                  <Field label={t('signup.lastName')} required>
                    <input
                      type="text"
                      value={form.last_name}
                      onChange={set('last_name')}
                      placeholder={t('signup.lastNamePlaceholder')}
                      required
                      className={inputCls}
                    />
                  </Field>
                </div>

                <Field label={t('signup.companyName')} required>
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={set('company_name')}
                    placeholder={t('signup.companyNamePlaceholder')}
                    required
                    className={inputCls}
                  />
                </Field>

                <Field label={t('signup.jobTitle')} required>
                  <input
                    type="text"
                    value={form.job_title}
                    onChange={set('job_title')}
                    placeholder={t('signup.jobTitlePlaceholder')}
                    required
                    className={inputCls}
                  />
                </Field>

                <Field label={t('signup.country')} required>
                  <CountrySelect
                    value={form.country}
                    onChange={(v) => {
                      setForm((f) => ({ ...f, country: v }));
                      setError('');
                    }}
                    placeholder={t('signup.countryPlaceholder')}
                    required
                  />
                </Field>

                <Field label={t('signup.hearAbout')}>
                  <select
                    value={form.referral_source}
                    onChange={set('referral_source')}
                    className={inputCls}
                  >
                    <option value="">{t('signup.hearAboutPlaceholder')}</option>
                    <option value="Google Search">{t('signup.hearAboutGoogleSearch')}</option>
                    <option value="LinkedIn">{t('signup.hearAboutLinkedIn')}</option>
                    <option value="Word of mouth">{t('signup.hearAboutWordOfMouth')}</option>
                    <option value="An event">{t('signup.hearAboutEvent')}</option>
                    <option value="A colleague">{t('signup.hearAboutColleague')}</option>
                    <option value="Other">{t('signup.hearAboutOther')}</option>
                  </select>
                </Field>

                {emailExistsError ? <EmailExistsErrorBox t={t} /> : error && <ErrorBox message={error} />}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 py-3 rounded-xl border border-gray-200 text-slate-600 hover:border-gray-300 hover:bg-gray-50 text-sm font-medium transition-colors"
                  >
                    {t('signup.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !form.country}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm transition-colors disabled:opacity-40"
                  >
                    {isLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('signup.submitting')}
                      </>
                    ) : (
                      t('signup.createAccount')
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            By continuing, you agree to our{' '}
            <a href="https://clareefai.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">
              Terms
            </a>{' '}
            and{' '}
            <a href="https://clareefai.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-violet-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
      {message}
    </div>
  );
}

function EmailExistsErrorBox({ t }: { t: (key: string) => string }) {
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
      {t('signup.emailExists')}{' '}
      <Link to="/login" className="font-semibold underline hover:text-red-800">
        {t('signup.logInInstead')}
      </Link>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}
